import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, ActivityIndicator, Modal } from "react-native";
import { Menu } from "react-native-paper";
import { useAppState, actions } from "../contexts/AppStateContext";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import DeleteResourceModalContainer from "../components/content/ContentModalContainers/DeleteResourceModalContainer";
import NewCustomAxioActionModal from "../containers/settings/UsersTabContainer/NewCustomAxioActionModal";
import ImageUpload from "../components/common/general/ImageUpload/ImageUpload";
import CustomModal from "../components/common/general/CustomModal/CustomModal";
import CourseRosterTabContainer from "../containers/courseSettings/RosterManagementContainer/RosterManagementContainer";
import ModuleContainer from "../containers/customCoursework/ModuleContainer";
import ActivityContainer from "../containers/customCoursework/ActivityContainer";
import AddModuleModalContainer from "../components/content/ContentModalContainers/AddModuleModalContainer";
import EditActivityModalContainer from "../containers/customCoursework/EditActivityModalContainer/EditActivityModalContainer";
import {
  GetCourseByIdDocument,
  useCreateAiTutorMutation,
  useDeleteModuleCascadeMutation,
  useDeleteAiTutorMutation,
  useCreateAxioResourceMutation,
  useUpdateAxioResourceMutation,
  useDeleteAxioResourceMutation,
  useUpdateCourseMutation,
  useUpdateResourceOrderMutation,
  useGenerateCourseContentMutation,
  useCreateAssignmentMutation,
  useDeleteAssignmentMutation,
  useEditAssignmentMutation,
  useMoveActivityMutation,
  useUpdateAiTutorMutation,
  useCreateReviewMutation,
  useDeleteReviewMutation,
  useGenerateLessonPlanMutation,
  useGetLessonPlanStatusLazyQuery,
  useDownloadLessonPlanMutation,
  Module,
  useGenerateAssessmentQuestionsMutation,
  useDeleteAssessmentMutation,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useUpdateReviewMutation,
  StoragePathType,
} from "../graphql/generated/graphql";
import Sentry from "../utils/sentry";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import CourseGeneratorSettingsModalContainer from "../containers/customCoursework/CourseGeneratorSettingsModal/CourseGeneratorSettingsModalContainer";
import { CustomCourseworkModulesViewModalState, ResourceType, useActiveCourse } from "../contexts/ActiveCourseContext";
import useFileFetch from "../hooks/useFileFetch";

function CustomCourseworkModulesView() {
  const { dispatch } = useAppState();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const {
    params: { courseId },
  } = useRoute<RouteProp<RootStackParamList, "Custom Coursework Modules">>();

  // States
  const [activityModalError, setActivityModalError] = useState(null);

  const {
    setCourseId,
    modalState,
    setModalState,
    courseData,
    refetchCourse,
    courseLoading,
    isSubmitting,
    setIsSubmitting,
  } = useActiveCourse();

  const courseModules = courseData?.getCourseById?.data?.[0]?.modules;
  const activeModuleId = modalState?.activeModule?.id;

  useEffect(() => {
    const modules = courseModules ?? [];
    const hasModules = modules.length > 0;
    const activeModuleStillExists = modules.some((module) => module?.id === activeModuleId);

    if (!hasModules || (activeModuleId && !activeModuleStillExists)) {
      if (modalState.activeModule !== null || modalState.activeActivity !== null) {
        setModalState((prevState) => ({
          ...prevState,
          activeModule: null,
          activeActivity: null,
        }));
      }
    }
  }, [courseModules, activeModuleId, modalState.activeModule, modalState.activeActivity, setModalState]);

  useEffect(() => {
    setCourseId(courseId);
  }, [courseId]);

  useEffect(() => {
    if (courseId) {
      refetchCourse();
    }
  }, [courseId, refetchCourse]);

  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  /*
  --- Lesson Plan Logic ---
  */

  const [getLessonPlanStatus] = useGetLessonPlanStatusLazyQuery();
  const [downloadLessonPlanMutation] = useDownloadLessonPlanMutation();

  const [lessonPlanStatuses, setLessonPlanStatuses] = useState<Record<string, unknown>>({});
  const [lessonPlanErrors, setLessonPlanErrors] = useState<Record<string, string>>({});

  const fetchLessonPlanStatuses = useCallback(async () => {
    if (courseModules) {
      courseModules.forEach(async (module) => {
        if (!module?.id) return;
        try {
          const { data } = await getLessonPlanStatus({
            variables: { moduleId: module.id },
          });

          if (data?.getLessonPlanStatus?.success) {
            setLessonPlanStatuses((prev) => ({
              ...prev,
              [module.id]: data.getLessonPlanStatus.data,
            }));
          }
        } catch (error) {
          console.error(`Error fetching lesson plan status for module ${module.id}:`, error);
        }
      });
    }
  }, [courseModules, getLessonPlanStatus]);

  useEffect(() => {
    fetchLessonPlanStatuses();
  }, [fetchLessonPlanStatuses]);

  /*
	--- End of Lesson Plan Logic ---
	*/

  /*
	--- Create Mutations ---
	*/

  const [createAiTutorMutation] = useCreateAiTutorMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [createAxioResourceMutation] = useCreateAxioResourceMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [createAssignmentMutation] = useCreateAssignmentMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [createReviewMutation] = useCreateReviewMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [createProjectMutation] = useCreateProjectMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [updateProjectMutation] = useUpdateProjectMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  /*
  --- Update Mutations ---
  */
  const [updateCourseMutation] = useUpdateCourseMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [updateAiTutorMutation] = useUpdateAiTutorMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [updateAxioResourceMutation] = useUpdateAxioResourceMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [updateResourceOrderMutation] = useUpdateResourceOrderMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [editAssignmentMutation] = useEditAssignmentMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [moveActivityMutation] = useMoveActivityMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [updateReviewMutation] = useUpdateReviewMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  /*
  --- Delete Mutations ---
  */
  const [deleteModuleMutation] = useDeleteModuleCascadeMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [deleteAiTutorMutation] = useDeleteAiTutorMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [deleteAxioResourceMutation] = useDeleteAxioResourceMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [deleteAssignmentMutation] = useDeleteAssignmentMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [deleteReviewMutation] = useDeleteReviewMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [deleteAssessmentMutation] = useDeleteAssessmentMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [deleteProjectMutation] = useDeleteProjectMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  /*
  --- Generate Mutations ---
  */
  const [generateCourseContentMutation] = useGenerateCourseContentMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [generateLessonPlanMutation] = useGenerateLessonPlanMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  const [generateAssessmentQuestionsMutation] = useGenerateAssessmentQuestionsMutation({
    refetchQueries: [{ query: GetCourseByIdDocument, variables: { id: courseId, withModules: true } }],
  });

  /*
  --- End of Mutations ---
  */

  // Activity Handlers
  const handleSaveActivity = async (activityData) => {
    setIsSubmitting(true);
    try {
      const {
        title,
        description,
        objectives,
        files,
        videoUrl,
        type,
        targetAudience,
        id,
        context,
        enableFileUpload,
        dueDate,
        videoPool = [],
        assessmentId,
        worksheetId,
        activityState,
        scheduledDate,
      } = activityData;

      const formattedDueDate = dueDate === "" ? null : undefined;

      if (type !== "Review" && type !== "Assessment" && !title) {
        setActivityModalError("Title is required");
        throw new Error("Title is required");
      }

      const cleanedDescription = description?.replace(/\n\s+/g, "").trim() || "";
      const encodedDescription = encodeURIComponent(cleanedDescription);

      // Safe decode function to handle malformed URI encoded descriptions
      const safeDecode = (value) => {
        try {
          return decodeURIComponent(value);
        } catch {
          console.warn("Malformed description detected:", value);
          return value;
        }
      };

      // Process videoPool data for Interactive Learning Experience activities
      if (type === "Interactive Learning Experience" && videoPool.length > 0) {
        if (!cleanedDescription) {
          setActivityModalError("Description is required");
          throw new Error("Description is required");
        }
        // Format video data to match the VideoDataInput type
        const formattedVideoPool = videoPool.map((video) => {
          // Find the objective by text to get its sequence
          const objective = objectives.find(
            (obj, index) =>
              obj === video.objectiveId || (typeof obj === "object" && obj.objective === video.objectiveId)
          );

          // Get the sequence either from the objective object or its index
          const sequence = typeof objective === "object" ? objective.sequence : objectives.indexOf(objective) + 1;

          return {
            videoId: video.videoId,
            objectiveText: video.objectiveId || video.objectiveText, // Support both formats
            objectiveSequence: sequence,
            isTeacherAdded: video.isTeacherAdded === true, // Explicitly check for true value
          };
        });

        // Update the videoPool in activityData
        activityData.videoPool = formattedVideoPool;
      }

      if (type === "Assignment") {
        const preparedFiles =
          files?.map((file) => ({
            fileId: file.fileId || file.id || String(Date.now()),
            fileName: file.fileName,
            fileData: file.fileData,
            contentType: file.contentType,
            size: file.size,
          })) || [];

        const isNewActivity = !id;
        let response;

        if (isNewActivity) {
          response = await createAssignmentMutation({
            variables: {
              assignment: {
                name: title,
                description: encodedDescription,
                files: preparedFiles,
                videoUrl: videoUrl || "",
                enableStudentFileUpload: enableFileUpload,
                dueDate: dueDate || "",
                moduleId: modalState.activeModule.id,
                worksheetId,
                activityState,
                scheduledDate,
              },
            },
          });
        } else {
          const origDescription = safeDecode(modalState.activeActivity.description || "");

          response = await editAssignmentMutation({
            variables: {
              id,
              assignment: {
                ...(title !== modalState.activeActivity.name &&
                  title !== modalState.activeActivity.title && { name: title }),
                ...(cleanedDescription !== origDescription && { description: encodedDescription }),
                files: preparedFiles,
                ...(videoUrl !== modalState.activeActivity.videoUrl && { videoUrl: videoUrl || "" }),
                ...(enableFileUpload !== modalState.activeActivity.enableStudentFileUpload && {
                  enableStudentFileUpload: enableFileUpload,
                }),
                dueDate: formattedDueDate,
                worksheetId,
                activityState,
                scheduledDate,
              },
            },
          });
        }

        if (
          (isNewActivity && response?.data?.createAssignment?.success) ||
          (!isNewActivity && response?.data?.editAssignment?.success)
        ) {
          dispatch({
            type: actions.SET_META,
            payload: { keypressIsListening: true },
          });
          setModalState((prevState) => ({
            ...prevState,
            state: CustomCourseworkModulesViewModalState.CLOSED,
            resourceType: null,
            resourceId: null,
            activeActivity: null,
          }));
          setActivityModalError(null);
        } else {
          const errorMessage = isNewActivity
            ? response?.data?.createAssignment?.message || "Failed to create assignment"
            : response?.data?.editAssignment?.message || "Failed to update assignment";

          setActivityModalError(errorMessage);
          throw new Error(errorMessage);
        }
      } else if (type === "Resource") {
        if (!modalState.activeModule?.id) {
          setActivityModalError("Module ID is required");
          throw new Error("Module ID is required");
        }

        const preparedFiles =
          files?.map((file) => ({
            fileId: file.fileId || file.id || String(Date.now()),
            fileName: file.fileName,
            fileData: file.fileData,
            contentType: file.contentType,
            size: file.size,
          })) || [];

        if (id) {
          const response = await updateAxioResourceMutation({
            variables: {
              id,
              resource: {
                name: title,
                description: encodedDescription,
                files: preparedFiles,
                videoUrl: videoUrl || "",
                order: (modalState.activeActivity?.order as number) || 0,
                activityState,
                scheduledDate,
              },
            },
          });

          if (response?.data?.updateAxioResource?.success) {
            dispatch({
              type: actions.SET_META,
              payload: { keypressIsListening: true },
            });

            setModalState((prevState) => ({
              ...prevState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceType: null,
              resourceId: null,
              activeActivity: null,
            }));

            setActivityModalError(null);
          } else {
            setActivityModalError(response?.data?.updateAxioResource?.message || "Failed to edit resource");
            throw new Error(response?.data?.updateAxioResource?.message || "Failed to edit resource");
          }
        } else {
          const response = await createAxioResourceMutation({
            variables: {
              resource: {
                moduleId: modalState.activeModule.id,
                order: (modalState.activeActivity?.order as number) || 0,
                name: title,
                description: encodedDescription,
                files: preparedFiles,
                videoUrl: videoUrl || "",
                activityState,
                scheduledDate,
              },
            },
          });

          if (response?.data?.createAxioResource?.success) {
            dispatch({
              type: actions.SET_META,
              payload: { keypressIsListening: true },
            });
            setModalState((prevState) => ({
              ...prevState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceType: null,
              resourceId: null,
              activeActivity: null,
            }));

            setActivityModalError(null);
          } else {
            setActivityModalError(response?.data?.createAxioResource?.message || "Failed to create resource");
            throw new Error(response?.data?.createAxioResource?.message || "Failed to create resource");
          }
        }
      } else if (type === "Interactive Learning Experience") {
        if (!objectives || objectives.length === 0) {
          setActivityModalError("Objectives are required");
          throw new Error("Objectives are required");
        }

        if (!cleanedDescription) {
          setActivityModalError("Description is required");
          throw new Error("Description is required");
        }

        // Objectives now come with evalCriteria already formatted
        const formattedObjectives = objectives;

        if (!modalState.activeModule?.id) {
          setActivityModalError("Parent module ID is required");
          throw new Error("Parent module ID is required");
        }

        if (id) {
          const response = await updateAiTutorMutation({
            variables: {
              id,
              aiTutor: {
                title,
                description: encodedDescription,
                objectives: formattedObjectives,
                context: context,
                visualizationId: activityData?.visualizationId || null,
                videoPool: videoPool || [],
                targetAudience,
                dueDate: dueDate || null,
                activityState,
                scheduledDate,
              },
            },
          });

          if (response?.data?.updateAITutor?.success) {
            dispatch({
              type: actions.SET_META,
              payload: { keypressIsListening: true },
            });

            setModalState((prevState) => ({
              ...prevState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceType: null,
              resourceId: null,
              activeActivity: null,
            }));

            setActivityModalError(null);
          } else {
            setActivityModalError(response?.data?.updateAITutor?.message || "Failed to update AI Tutor");
            throw new Error(response?.data?.updateAITutor?.message || "Failed to update AI Tutor");
          }
          setIsSubmitting(false);
          return;
        }

        const response = await createAiTutorMutation({
          variables: {
            title,
            description: encodedDescription,
            parentModuleId: modalState.activeModule.id,
            objectives: formattedObjectives,
            context: context,
            visualizationId: activityData?.visualizationId || null,
            videoPool: activityData?.videoPool || [],
            targetAudience: targetAudience || "",
            dueDate: dueDate || null,
            activityState,
            scheduledDate,
          },
        });

        if (response?.data?.createAITutor?.success) {
          dispatch({
            type: actions.SET_META,
            payload: { keypressIsListening: true },
          });

          setModalState((prevState) => ({
            ...prevState,
            state: CustomCourseworkModulesViewModalState.CLOSED,
            resourceType: null,
            resourceId: null,
            activeActivity: null,
          }));

          setActivityModalError(null);
        } else {
          setActivityModalError(response?.data?.createAITutor?.message || "Failed to create AI Tutor");
          throw new Error(response?.data?.createAITutor?.message || "Failed to create AI Tutor");
        }
      } else if (type === "Review") {
        if (!modalState.activeModule?.id) {
          setActivityModalError("Module ID is required");
          throw new Error("Module ID is required");
        }

        if (!activityData.assessmentId) {
          setActivityModalError("Assessment ID is required");
          throw new Error("Assessment ID is required");
        }

        if (id) {
          const response = await updateReviewMutation({
            variables: {
              id,
              review: {
                assessmentId: activityData.assessmentId,
                activityState,
                scheduledDate,
              },
            },
          });

          if (response?.data?.updateReview?.success) {
            dispatch({
              type: actions.SET_META,
              payload: { keypressIsListening: true },
            });

            setModalState({
              ...modalState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceType: null,
              resourceId: null,
              activeActivity: null,
            });
            setActivityModalError(null);
            setIsSubmitting(false);
            return;
          } else {
            setActivityModalError(response?.data?.updateReview?.message || "Failed to update review");
            throw new Error(response?.data?.updateReview?.message || "Failed to update review");
          }
        }

        const response = await createReviewMutation({
          variables: {
            review: {
              parentModuleId: modalState.activeModule.id,
              assessmentId: activityData.assessmentId,
              activityState,
              scheduledDate,
            },
          },
        });

        if (response?.data?.createReview?.success) {
          dispatch({
            type: actions.SET_META,
            payload: { keypressIsListening: true },
          });

          setModalState({
            ...modalState,
            state: CustomCourseworkModulesViewModalState.CLOSED,
            resourceType: null,
            resourceId: null,
            activeActivity: null,
          });

          setActivityModalError(null);
        } else {
          setActivityModalError(response?.data?.createReview?.message || "Failed to create review");
          throw new Error(response?.data?.createReview?.message || "Failed to create review");
        }
      } else if (type === "Assessment") {
        if (!activityData.assessmentId) {
          setActivityModalError("Assessment ID is required");
          throw new Error("Assessment ID is required");
        }

        if (!activityData.aiTutorIds || activityData.aiTutorIds.length === 0) {
          setActivityModalError("AI Tutor IDs are required");
          throw new Error("AI Tutor IDs are required");
        }

        const response = await generateAssessmentQuestionsMutation({
          variables: {
            assessmentId: activityData.assessmentId,
            numberOfQuestions: activityData.numberOfQuestions || 10,
            timeLimit: activityData.timeLimit || 25,
            aiTutorIds: activityData.aiTutorIds,
            activityState: activityData.activityState,
            scheduledDate: activityData.scheduledDate,
          },
        });

        if (response?.data?.generateAssessmentQuestions?.success) {
          dispatch({
            type: actions.SET_META,
            payload: { keypressIsListening: true },
          });

          setModalState({
            ...modalState,
            state: CustomCourseworkModulesViewModalState.CLOSED,
            resourceType: null,
            resourceId: null,
            activeActivity: null,
          });

          setActivityModalError(null);
        } else {
          setActivityModalError(
            response?.data?.generateAssessmentQuestions?.message || "Failed to generate assessment questions"
          );
          throw new Error(
            response?.data?.generateAssessmentQuestions?.message || "Failed to generate assessment questions"
          );
        }
      } else if (type === "Project") {
        if (!modalState.activeModule?.id) {
          setActivityModalError("Module ID is required");
          throw new Error("Module ID is required");
        }

        if (!title) {
          setActivityModalError("Project name is required");
          throw new Error("Project name is required");
        }

        if (!cleanedDescription) {
          setActivityModalError("Project description is required");
          throw new Error("Project description is required");
        }

        // Extract project-specific fields from activityData
        const { scopeDocument, deliverableTypes, assessmentCriteria, expectedOutcomes } = activityData;

        if (id) {
          const response = await updateProjectMutation({
            variables: {
              id: id,
              project: {
                name: title,
                description: cleanedDescription,
                scopeDocument: scopeDocument || "",
                deliverableTypes: deliverableTypes || [],
                assessmentCriteria: assessmentCriteria || [],
                expectedOutcomes: expectedOutcomes || [],
                dueDate: dueDate || null,
                activityState,
                scheduledDate,
                files,
              },
            },
          });

          if (response?.data?.updateProject?.success) {
            dispatch({
              type: actions.SET_META,
              payload: { keypressIsListening: true },
            });

            setModalState({
              ...modalState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceType: null,
              resourceId: null,
              activeActivity: null,
            });

            setActivityModalError(null);
          } else {
            setActivityModalError(response?.data?.updateProject?.message || "Failed to update project");
            throw new Error(response?.data?.updateProject?.message || "Failed to update project");
          }
          setIsSubmitting(false);
          return;
        }

        const response = await createProjectMutation({
          variables: {
            project: {
              name: title,
              description: cleanedDescription,
              moduleId: modalState.activeModule.id,
              scopeDocument: scopeDocument || "",
              deliverableTypes: deliverableTypes || [],
              assessmentCriteria: assessmentCriteria || [],
              expectedOutcomes: expectedOutcomes || [],
              order: 0,
              dueDate: dueDate || null,
              activityState,
              scheduledDate,
              files,
            },
          },
        });

        if (response?.data?.createProject?.success) {
          dispatch({
            type: actions.SET_META,
            payload: { keypressIsListening: true },
          });

          setModalState({
            ...modalState,
            state: CustomCourseworkModulesViewModalState.CLOSED,
            resourceType: null,
            resourceId: null,
            activeActivity: null,
          });

          setActivityModalError(null);
        } else {
          setActivityModalError(response?.data?.createProject?.message || "Failed to create project");
          throw new Error(response?.data?.createProject?.message || "Failed to create project");
        }
      }
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error saving activity:", error);
      Sentry.captureException(error);
      setIsSubmitting(false);
      throw error;
    }
  };

  const handleMoveActivity = async (activityId, sourceModuleId, targetModuleId, activityType) => {
    try {
      const response = await moveActivityMutation({
        variables: {
          activityId,
          sourceModuleId,
          targetModuleId,
          activityType,
        },
      });

      if (!response?.data?.moveActivity?.success) {
        throw new Error(response?.data?.moveActivity?.message || "Failed to move activity");
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error moving activity:", error);
    }
  };

  const handleOpenAddActivity = () => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setModalState((prevState) => ({
      ...prevState,
      state: CustomCourseworkModulesViewModalState.ADD_OR_EDIT_ACTIVITY,
      resourceType: ResourceType.MODULE,
      resourceId: null,
      activeActivity: null,
    }));
  };

  // Module Handlers
  const handleDeleteModule = async (module) => {
    setModalState((prevState) => ({
      ...prevState,
      state: CustomCourseworkModulesViewModalState.DELETE_MODULE_OR_ACTIVITY,
      resourceType: ResourceType.MODULE,
      resourceId: module.id,
    }));
  };

  const handleEditModule = (module) => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setModalState((prevState) => ({
      ...prevState,
      activeModule: module,
      state: CustomCourseworkModulesViewModalState.EDIT_MODULE_NAME,
      resourceType: ResourceType.MODULE,
      resourceId: module.id,
    }));
  };

  // General Handlers
  const handleConfirmDelete = async () => {
    if (modalState.resourceType === ResourceType.MODULE) {
      try {
        const response = await deleteModuleMutation({
          variables: {
            id: modalState.resourceId,
          },
        });

        if (response?.data?.deleteModuleCascade?.success) {
          setModalState({
            state: CustomCourseworkModulesViewModalState.CLOSED,
            resourceId: null,
            resourceType: null,
            activeModule: courseData?.getCourseById?.data?.[0]?.modules?.[0] || null,
            activeActivity: null,
          });
        } else {
          throw new Error(response?.data?.deleteModuleCascade?.message || "Failed to delete module");
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error deleting module:", error);
      }
    } else if (
      modalState.resourceType === ResourceType.ACTIVITY_RESOURCE ||
      modalState.resourceType === ResourceType.ACTIVITY_ASSIGNMENT ||
      modalState.resourceType === ResourceType.ACTIVITY_INTERACTIVE_LEARNING_EXPERIENCE ||
      modalState.resourceType === ResourceType.ACTIVITY_PAGE ||
      modalState.resourceType === ResourceType.ACTIVITY_REVIEW ||
      modalState.resourceType === ResourceType.ACTIVITY_ASSESSMENT ||
      modalState.resourceType === ResourceType.ACTIVITY_PROJECT
    ) {
      try {
        if (modalState.resourceType === ResourceType.ACTIVITY_RESOURCE) {
          const response = await deleteAxioResourceMutation({
            variables: {
              id: modalState.resourceId,
            },
          });

          if (response?.data?.deleteAxioResource?.success) {
            setModalState((prevState) => ({
              ...prevState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceId: null,
              resourceType: null,
              activeActivity: null,
            }));
          } else {
            throw new Error(response?.data?.deleteAxioResource?.message || "Failed to delete resource");
          }
        } else if (modalState.resourceType === ResourceType.ACTIVITY_ASSIGNMENT) {
          const response = await deleteAssignmentMutation({
            variables: {
              id: modalState.resourceId,
            },
          });

          if (response?.data?.deleteAssignment?.success) {
            setModalState((prevState) => ({
              ...prevState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceId: null,
              resourceType: null,
              activeActivity: null,
            }));
          } else {
            throw new Error("Failed to delete assignment");
          }
        } else if (modalState.resourceType === ResourceType.ACTIVITY_INTERACTIVE_LEARNING_EXPERIENCE) {
          const response = await deleteAiTutorMutation({
            variables: {
              id: modalState.resourceId,
            },
          });

          if (response?.data?.deleteAITutor?.success) {
            setModalState((prevState) => ({
              ...prevState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceId: null,
              resourceType: null,
              activeActivity: null,
            }));
          } else {
            throw new Error(response?.data?.deleteAITutor?.message || "Failed to delete activity");
          }
        } else if (modalState.resourceType === ResourceType.ACTIVITY_REVIEW) {
          const response = await deleteReviewMutation({
            variables: {
              id: modalState.resourceId,
            },
          });

          if (response?.data?.deleteReview?.success) {
            setModalState({
              ...modalState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceId: null,
              resourceType: null,
              activeActivity: null,
            });
          } else {
            throw new Error(response?.data?.deleteReview?.message || "Failed to delete review");
          }
        } else if (modalState.resourceType === ResourceType.ACTIVITY_ASSESSMENT) {
          const response = await deleteAssessmentMutation({
            variables: {
              id: modalState.resourceId,
            },
          });

          if (response?.data?.deleteAssessment?.success) {
            setModalState({
              ...modalState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceId: null,
              resourceType: null,
              activeActivity: null,
            });
          } else {
            throw new Error(response?.data?.deleteAssessment?.message || "Failed to delete assessment");
          }
        } else if (modalState.resourceType === ResourceType.ACTIVITY_PROJECT) {
          const response = await deleteProjectMutation({
            variables: {
              id: modalState.resourceId,
            },
          });

          if (response?.data?.deleteProject?.success) {
            setModalState({
              ...modalState,
              state: CustomCourseworkModulesViewModalState.CLOSED,
              resourceId: null,
              resourceType: null,
              activeActivity: null,
            });
          } else {
            throw new Error(response?.data?.deleteProject?.message || "Failed to delete project");
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error deleting activity:", error);
      }
    }
  };

  const handleItemOptionsPress = (action, activity) => {
    if (action === "edit") {
      dispatch({
        type: actions.SET_META,
        payload: { keypressIsListening: false },
      });
      setModalState((prevState) => ({
        ...prevState,
        state: CustomCourseworkModulesViewModalState.ADD_OR_EDIT_ACTIVITY,
        resourceId: activity.id,
        resourceType: activity.type as ResourceType,
        activeActivity: activity,
      }));
    } else if (action === "delete") {
      // Determine the correct resource type
      let resourceType;
      const activityType = activity.type || activity.__typename;

      switch (activityType) {
        case "Resource":
        case "AxioResource":
          resourceType = ResourceType.ACTIVITY_RESOURCE;
          break;
        case "Assignment":
          resourceType = ResourceType.ACTIVITY_ASSIGNMENT;
          break;
        case "Interactive Learning Experience":
        case "AITutor":
          resourceType = ResourceType.ACTIVITY_INTERACTIVE_LEARNING_EXPERIENCE;
          break;
        case "Review":
          resourceType = ResourceType.ACTIVITY_REVIEW;
          break;
        case "Assessment":
          resourceType = ResourceType.ACTIVITY_ASSESSMENT;
          break;
        case "Project":
          resourceType = ResourceType.ACTIVITY_PROJECT;
          break;
        default:
          resourceType = activityType as ResourceType;
      }

      setModalState((prevState) => ({
        ...prevState,
        state: CustomCourseworkModulesViewModalState.DELETE_MODULE_OR_ACTIVITY,
        resourceType: resourceType,
        resourceId: activity.id,
      }));
    } else if (action === "clone") {
      handleCloneActivity(activity);
    }
  };

  const handleCloneActivity = (activity) => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });

    // Determine the activity type correctly
    let activityType = activity.type;

    // For AI Tutors, ensure we're using the correct type name
    if (activity.__typename === "AITutor" || activityType === "AITutor") {
      activityType = "Interactive Learning Experience";
    } else if (activity.__typename === "AxioResource") {
      activityType = "Resource";
    }

    // Dump absolutely ALL properties
    function dumpObject(obj, path = "", level = 0) {
      if (!obj || typeof obj !== "object" || level > 5) return;

      for (const key in obj) {
        try {
          const value = obj[key];
          const currentPath = path ? `${path}.${key}` : key;

          if (typeof value === "object" && value !== null) {
            dumpObject(value, currentPath, level + 1);
          }
        } catch (err) {
          console.log(`Error accessing property ${key}: ${err.message}`);
        }
      }
    }

    dumpObject(activity);

    // Process objectives and evaluation criteria
    let objectives = [];
    let evalCriteria = [];
    let criteriaFound = false;

    // Try multiple approaches to find the evaluation criteria

    // Check for formattedObjectives which are present when creating or editing activities
    // This is the primary source for evaluation criteria, coming from the UI state
    if (activity.formattedObjectives && Array.isArray(activity.formattedObjectives)) {
      criteriaFound = true;

      objectives = activity.formattedObjectives.map((obj) => obj.objective);

      evalCriteria = activity.formattedObjectives.map((obj, index) => {
        return {
          id: `obj-${Math.random().toString(36).substring(2, 11)}`,
          objective: obj.objective,
          sequence: index + 1,
          evalCriteria: Array.isArray(obj.evalCriteria) ? [...obj.evalCriteria] : [],
          status: "notStarted",
        };
      });
    }

    // Also look for criteria in the newly generated case where they might be in activity.__generatedObjectives
    if (!criteriaFound && activity.__generatedObjectives && Array.isArray(activity.__generatedObjectives)) {
      criteriaFound = true;

      objectives = activity.__generatedObjectives.map((obj) => obj.objective);

      evalCriteria = activity.__generatedObjectives.map((obj, index) => {
        return {
          id: `obj-${Math.random().toString(36).substring(2, 11)}`,
          objective: obj.objective,
          sequence: index + 1,
          evalCriteria: Array.isArray(obj.evalCriteria) ? [...obj.evalCriteria] : [],
          status: "notStarted",
        };
      });
    }

    // Check for raw objectives with embedded criteria
    if (!criteriaFound && activity.objectives && activity.objectives.length > 0) {
      // Extract simple objectives array for display
      objectives = activity.objectives.map((obj) => {
        return typeof obj === "string" ? obj : obj.objective;
      });

      // Check if we have direct evalCriteria on objectives
      const objWithCriteria = activity.objectives.find(
        (obj) => typeof obj === "object" && obj.evalCriteria && Array.isArray(obj.evalCriteria)
      );

      if (objWithCriteria) {
        criteriaFound = true;

        evalCriteria = activity.objectives.map((obj, index) => {
          const objective = typeof obj === "string" ? obj : obj.objective;
          const criteria = typeof obj === "object" && Array.isArray(obj.evalCriteria) ? [...obj.evalCriteria] : [];

          return {
            id: `obj-${Math.random().toString(36).substring(2, 11)}`,
            objective: objective,
            sequence: index + 1,
            evalCriteria: criteria,
            status: "notStarted",
          };
        });
      }
    }

    // If still no criteria, look deeper into the activity structure
    if (!criteriaFound) {
      // Look for them in any property that might contain them
      const deepSearch = (obj, path = "") => {
        if (!obj || typeof obj !== "object") return;

        // Check if this object has a structure that looks like it contains evaluation criteria
        if (Array.isArray(obj) && obj.length > 0 && obj[0] && obj[0].objective && Array.isArray(obj[0].evalCriteria)) {
          criteriaFound = true;
          objectives = obj.map((o) => o.objective);

          evalCriteria = obj.map((o, index) => {
            return {
              id: `obj-${Math.random().toString(36).substring(2, 11)}`,
              objective: o.objective,
              sequence: index + 1,
              evalCriteria: Array.isArray(o.evalCriteria) ? [...o.evalCriteria] : [],
              status: "notStarted",
            };
          });

          return;
        }

        // Continue searching recursively
        Object.keys(obj).forEach((key) => {
          deepSearch(obj[key], `${path}.${key}`);
        });
      };

      deepSearch(activity);
    }

    // If we still couldn't find criteria, create empty ones as a last resort
    if (!criteriaFound) {
      evalCriteria = objectives.map((objective, index) => {
        return {
          id: `obj-${Math.random().toString(36).substring(2, 11)}`,
          objective: objective,
          sequence: index + 1,
          evalCriteria: [],
          status: "notStarted",
        };
      });
    }

    // Create a comprehensive clone of the activity
    const clonedActivity = {
      ...activity,
      title: `${activity.title} (Copy)`, // Add "(Copy)" to the title
      id: undefined, // Remove ID so a new activity will be created
      type: activityType, // Ensure the type is explicitly set

      // Include all necessary fields
      description: activity.description || "",
      objectives: objectives,
      files: activity.files || [],
      videoUrl: activity.videoUrl || "",
      dueDate: activity.dueDate || "",
      enableFileUpload: activity.enableFileUpload || false,
      context: activity.context || "",

      // Mark as a clone to help EditActivityModalContainer process it correctly
      _isClone: true,

      // Always include evalCriteria - preserve the structure from the original
      evalCriteria: evalCriteria,

      // Also include the formattedObjectives if they were found
      formattedObjectives: activity.formattedObjectives,

      // Include visualization data
      visualizationId: activity.visualization?.id || null,

      // Include videoPool with isTeacherAdded flag preserved
      videoPool: Array.isArray(activity.videoPool)
        ? activity.videoPool.map((video) => ({
            ...video,
            isTeacherAdded: video.isTeacherAdded || false,
          }))
        : [],

      // If this is a resource, ensure we handle name/title mapping
      name: activity.name || activity.title, // Some resources use name instead of title
    };

    setModalState((prevState) => ({
      ...prevState,
      state: CustomCourseworkModulesViewModalState.ADD_OR_EDIT_ACTIVITY,
      resourceType: ResourceType.MODULE,
      resourceId: null,
      activeActivity: clonedActivity,
    }));
  };

  const handleGenerateCourse = async (
    numberOfModules: number,
    numberOfActivities: number,
    courseDescription?: string
  ) => {
    setIsSubmitting(true);
    try {
      setModalState((prevState) => ({
        ...prevState,
        state: CustomCourseworkModulesViewModalState.COURSE_GENERATION_IN_PROGRESS,
      }));
      const result = await generateCourseContentMutation({
        variables: {
          courseId: courseId,
          numberOfModules,
          numberOfActivities,
          courseDescription,
        },
      });

      if (!result?.data?.generateCourseContent?.success) {
        throw new Error(result?.data?.generateCourseContent?.message || "Failed to generate course content");
      }
      setIsSubmitting(false);
    } catch (error) {
      setModalState((prevState) => ({
        ...prevState,
        state: CustomCourseworkModulesViewModalState.CLOSED,
      }));
      setIsSubmitting(false);
      Sentry.captureException(error);
      console.error("Error generating course content:", error);
    }
  };

  const handlePublishPress = async () => {
    setIsSubmitting(true);
    try {
      await updateCourseMutation({
        variables: {
          id: courseId,
          course: {
            isDraft: !courseData?.getCourseById?.data?.[0]?.isDraft,
          },
        },
      });
      setIsSubmitting(false);
      setModalState((prevState) => ({ ...prevState, state: CustomCourseworkModulesViewModalState.CLOSED }));
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error publishing course:", error);
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = async (thumbnailUrl: string) => {
    setIsSubmitting(true);
    try {
      const response = await updateCourseMutation({
        variables: {
          id: courseId,
          course: {
            thumbnail: thumbnailUrl,
          },
        },
      });

      if (response.data?.updateCourse?.success) {
        setModalState((prevState) => ({
          ...prevState,
          state: CustomCourseworkModulesViewModalState.EDIT_IMAGE_CONFIRMATION,
        }));
      } else {
        setModalState((prevState) => ({
          ...prevState,
          state: CustomCourseworkModulesViewModalState.EDIT_IMAGE_ERROR,
        }));
        throw new Error(response.data?.updateCourse?.message || "Failed to upload image");
      }
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      setModalState((prevState) => ({
        ...prevState,
        state: CustomCourseworkModulesViewModalState.EDIT_IMAGE_ERROR,
      }));
      setIsSubmitting(false);
    }
  };

  const handleExitPress = () => {
    setModalState((prevState) => ({
      ...prevState,
      state: CustomCourseworkModulesViewModalState.CLOSED,
    }));
    navigation.goBack();
  };

  const handleDragEnd = async (data) => {
    await updateResourceOrderMutation({
      variables: {
        input: {
          moduleId: modalState.activeModule.id,
          resourceOrder: data.map((item) => item.id),
        },
      },
    });
  };

  const handleGenerateLessonPlan = async (moduleId: string, selectedSections: string[]) => {
    setIsSubmitting(true);
    try {
      setLessonPlanErrors((prev) => ({ ...prev, [moduleId]: "" }));

      // Update local state to show generating
      setLessonPlanStatuses((prev) => ({
        ...prev,
        [moduleId]: {
          ...(prev[moduleId] as Record<string, unknown>),
          isGenerating: true,
          hasLessonPlan: false,
        },
      }));

      const response = await generateLessonPlanMutation({
        variables: {
          input: {
            moduleId,
            selectedSections,
          },
        },
      });

      if (response?.data?.generateLessonPlan?.success) {
        // Poll for status updates with a more frequent interval initially
        let pollCount = 0;
        const maxPolls = 100; // 5 minutes max (100 * 3 seconds)

        const pollStatus = setInterval(async () => {
          try {
            pollCount++;

            const { data } = await getLessonPlanStatus({
              variables: { moduleId },
              fetchPolicy: "network-only",
            });

            if (data?.getLessonPlanStatus?.success) {
              const status = data.getLessonPlanStatus.data;

              setLessonPlanStatuses((prev) => ({
                ...prev,
                [moduleId]: status,
              }));

              // Stop polling when generation is complete or failed
              if (!status.isGenerating || pollCount >= maxPolls) {
                clearInterval(pollStatus);

                // If it failed and no lesson plan exists, show error
                if (!status.isGenerating && !status.hasLessonPlan && pollCount < maxPolls) {
                  setLessonPlanErrors((prev) => ({
                    ...prev,
                    [moduleId]: "Error generating lesson plan",
                  }));
                }
              }
            }
          } catch (error) {
            console.error("Error polling lesson plan status:", error);
            clearInterval(pollStatus);

            // Set error state
            setLessonPlanStatuses((prev) => ({
              ...prev,
              [moduleId]: { ...(prev[moduleId] as Record<string, unknown>), isGenerating: false },
            }));

            setLessonPlanErrors((prev) => ({
              ...prev,
              [moduleId]: "Error generating lesson plan",
            }));
          }
        }, 3000);

        // Clear interval after 5 minutes max
        setTimeout(() => {
          clearInterval(pollStatus);
          setLessonPlanStatuses((prev) => {
            if ((prev[moduleId] as Record<string, unknown>)?.isGenerating) {
              setLessonPlanErrors((prevErrors) => ({
                ...prevErrors,
                [moduleId]: "Lesson plan generation timed out",
              }));
              return {
                ...prev,
                [moduleId]: { ...(prev[moduleId] as Record<string, unknown>), isGenerating: false },
              };
            }
            return prev;
          });
        }, 300000);
      } else {
        throw new Error(response?.data?.generateLessonPlan?.message || "Failed to generate lesson plan");
      }
    } catch (error) {
      console.error("Error generating lesson plan:", error);

      // Reset generating state on error
      setLessonPlanStatuses((prev) => ({
        ...prev,
        [moduleId]: { ...(prev[moduleId] as Record<string, unknown>), isGenerating: false },
      }));

      setLessonPlanErrors((prev) => ({
        ...prev,
        [moduleId]: "Error generating lesson plan",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const { fetchAndOpenFile } = useFileFetch();

  const handleDownloadLessonPlan = async (moduleId: string) => {
    try {
      const response = await downloadLessonPlanMutation({
        variables: { moduleId },
      });

      if (response?.data?.downloadLessonPlan?.success) {
        const downloadData = response.data.downloadLessonPlan.data;

        await fetchAndOpenFile(downloadData.downloadUrl);
      } else {
        throw new Error(response?.data?.downloadLessonPlan?.message || "Failed to download lesson plan");
      }
    } catch (error) {
      console.error("Error downloading lesson plan:", error);

      setLessonPlanErrors((prev) => ({
        ...prev,
        [moduleId]: "Error downloading lesson plan",
      }));
    }
  };

  if (courseLoading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0000ff" />
      </View>
    );
  }

  // If the course roster modal is visible, show the CourseRosterTabContainer
  if (modalState.state === CustomCourseworkModulesViewModalState.COURSE_ROSTER) {
    return (
      <CourseRosterTabContainer
        courseId={courseId}
        onBack={() => setModalState({ ...modalState, state: CustomCourseworkModulesViewModalState.CLOSED })}
      />
    );
  }

  return (
    <DefaultLayout menuType="customCoursework" pageHasChat={false} withPadding={false} showCourseNav={true}>
      <View style={{ flex: 1 }}>
        {courseLoading ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        ) : modalState.activeActivity ? (
          <ActivityContainer
            activity={modalState.activeActivity}
            onEdit={() => handleItemOptionsPress("edit", modalState.activeActivity)}
            onDelete={() => handleItemOptionsPress("delete", modalState.activeActivity)}
          />
        ) : modalState.activeModule ? (
          <ModuleContainer
            module={courseData?.getCourseById?.data?.[0]?.modules?.find(
              (module) => module.id === modalState.activeModule.id
            )}
            allModules={courseData?.getCourseById?.data?.[0]?.modules}
            onEditModule={handleEditModule}
            onDeleteModule={handleDeleteModule}
            onOpenAddActivity={handleOpenAddActivity}
            onItemOptionsPress={handleItemOptionsPress}
            onDragEnd={handleDragEnd}
            onMoveActivity={handleMoveActivity}
            onGenerateLessonPlan={handleGenerateLessonPlan}
            onDownloadLessonPlan={handleDownloadLessonPlan}
            lessonPlanStatus={lessonPlanStatuses[modalState.activeModule.id]}
            lessonPlanError={lessonPlanErrors[modalState.activeModule.id]}
          />
        ) : null}
      </View>
      {/* Modals */}
      <DeleteResourceModalContainer
        visible={modalState.state === CustomCourseworkModulesViewModalState.DELETE_MODULE_OR_ACTIVITY}
        onClose={() =>
          setModalState((prevState) => ({ ...prevState, state: CustomCourseworkModulesViewModalState.CLOSED }))
        }
        onDeleteResource={handleConfirmDelete}
        type={modalState.resourceType}
      />

      <EditActivityModalContainer
        visible={modalState.state === CustomCourseworkModulesViewModalState.ADD_OR_EDIT_ACTIVITY}
        onClose={() =>
          setModalState({
            ...modalState,
            state: CustomCourseworkModulesViewModalState.CLOSED,
            resourceType: null,
            resourceId: null,
            activeActivity: null,
          })
        }
        onSave={handleSaveActivity}
        error={activityModalError}
        existingActivity={modalState.activeActivity}
        isSubmitting={isSubmitting}
        courseId={courseId}
        activeModuleData={
          (courseData?.getCourseById?.data?.[0]?.modules?.find(
            (module) => module.id === modalState?.activeModule?.id
          ) as Module) || null
        }
      />

      <AddModuleModalContainer
        visible={
          modalState.state === CustomCourseworkModulesViewModalState.ADD_MODULE ||
          modalState.state === CustomCourseworkModulesViewModalState.EDIT_MODULE_NAME
        }
        setModalState={setModalState}
        courseId={courseId}
        modalState={modalState}
      />

      <CourseGeneratorSettingsModalContainer
        visible={modalState.state === CustomCourseworkModulesViewModalState.COURSE_GENERATION_DIALOG}
        onClose={() =>
          setModalState((prevState) => ({ ...prevState, state: CustomCourseworkModulesViewModalState.CLOSED }))
        }
        onGenerateCourse={handleGenerateCourse}
        isSubmitting={isSubmitting}
      />

      <Modal
        animationType="fade"
        transparent={true}
        visible={modalState.state === CustomCourseworkModulesViewModalState.COURSE_GENERATION_IN_PROGRESS}
        onRequestClose={() =>
          setModalState((prevState) => ({ ...prevState, state: CustomCourseworkModulesViewModalState.CLOSED }))
        }>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ActivityIndicator size="large" color="#0000ff" />
            <CustomSpacing type="vertical" size="m" />
            <CustomText text="Generating Course..." size="m" weight="bold" />
            <CustomSpacing type="vertical" size="m" />
            <CustomText
              text="This may take a few minutes. You can leave this page, and your course will be ready when you return."
              size="m"
              weight="bold"
              style={{ textAlign: "center", maxWidth: 450 }}
            />
            <CustomSpacing type="vertical" size="m" />
            <CustomButton size="s" text="Back to Courses" styleType="primary" onPress={handleExitPress} />
          </View>
        </View>
      </Modal>

      {/* Edit Image Modal */}
      <CustomModal
        visible={modalState.state === CustomCourseworkModulesViewModalState.EDIT_IMAGE}
        onRequestClose={() =>
          setModalState((prevState) => ({ ...prevState, state: CustomCourseworkModulesViewModalState.CLOSED }))
        }
        title="Edit Course Image"
        ariaLabel="Edit Course Image Modal"
        showCloseButton={true}
        style={{ width: "90%" }}
        exitFill={undefined}
        onShow={() => {}}>
        <View style={styles.modalContent}>
          <ImageUpload
            onChange={handleImageUpload}
            storagePath={StoragePathType.Thumbnails}
            aspectRatio={[16, 9]}
            buttonText="Upload Course Image"
            changeButtonText="Change Course Image"
            dropzoneText="Drag & drop course image here"
            dropzoneSubtext="Recommended: 1792x1024px. JPG or PNG format"
          />
          <View style={styles.buttonContainer}>
            <CustomButton
              text="Cancel"
              onPress={() =>
                setModalState((prevState) => ({ ...prevState, state: CustomCourseworkModulesViewModalState.CLOSED }))
              }
              styleType="secondary"
              size="m"
              style={isSubmitting ? { backgroundColor: "#ccc" } : styles.cancelButton}
              textStyle={styles.cancelButtonText}
              disabled={isSubmitting}
            />
          </View>
        </View>
      </CustomModal>

      {/* Image Update Confirmation Modals */}
      <NewCustomAxioActionModal
        visible={modalState.state === CustomCourseworkModulesViewModalState.EDIT_IMAGE_CONFIRMATION}
        onClose={() =>
          setModalState((prevState) => ({ ...prevState, state: CustomCourseworkModulesViewModalState.CLOSED }))
        }
        modalTexts={{
          title: "Success!",
          ariaLabel: "Course Image Updated Successfully Modal",
          warningText: "The course image has been successfully updated.",
          actionButtonText: "OK",
        }}
        contextualText={courseData?.getCourseById?.data?.[0]?.name}
      />

      <NewCustomAxioActionModal
        visible={modalState.state === CustomCourseworkModulesViewModalState.EDIT_IMAGE_ERROR}
        onClose={() =>
          setModalState((prevState) => ({ ...prevState, state: CustomCourseworkModulesViewModalState.CLOSED }))
        }
        modalTexts={{
          title: "Error!",
          ariaLabel: "Course Image Update Error Modal",
          warningText: "An error occurred while updating the course image.",
          actionButtonText: "CLOSE",
        }}
        contextualText={courseData?.getCourseById?.data?.[0]?.name}
      />

      <NewCustomAxioActionModal
        visible={modalState.state === CustomCourseworkModulesViewModalState.PUBLISH_COURSE}
        onClose={() =>
          setModalState((prevState) => ({ ...prevState, state: CustomCourseworkModulesViewModalState.CLOSED }))
        }
        modalTexts={{
          title: "Publish Course",
          ariaLabel: "Publish Course Modal",
          warningText: "Are you sure you want to publish this course? Users will be able to view this course.",
          actionButtonText: "Publish",
        }}
        onAction={handlePublishPress}
        contextualText={courseData?.getCourseById?.data?.[0]?.name}
      />

      <NewCustomAxioActionModal
        visible={modalState.state === CustomCourseworkModulesViewModalState.UNPUBLISH_COURSE}
        onClose={() =>
          setModalState((prevState) => ({ ...prevState, state: CustomCourseworkModulesViewModalState.CLOSED }))
        }
        modalTexts={{
          title: "Unpublish Course",
          ariaLabel: "Unpublish Course Modal",
          warningText:
            "Are you sure you want to unpublish this course? Users will no longer be able to view this course.",
          actionButtonText: "Unpublish",
        }}
        onAction={handlePublishPress}
        contextualText={courseData?.getCourseById?.data?.[0]?.name}
      />

      {/* Menu for Manage Course button */}
      <Menu
        visible={menuVisible}
        onDismiss={() => setMenuVisible(false)}
        anchor={menuPosition}
        contentStyle={{ minWidth: 150, backgroundColor: "#fff" }}
        style={{ marginTop: -10 }}>
        <Menu.Item
          onPress={() => {
            setMenuVisible(false);
            setModalState((prevState) => ({
              ...prevState,
              state: CustomCourseworkModulesViewModalState.EDIT_IMAGE,
            }));
          }}
          title="Edit Image"
          titleStyle={{ color: "#3E68FE" }}
        />
        <Menu.Item
          onPress={() => {
            setMenuVisible(false);
            setModalState((prevState) => ({
              ...prevState,
              state: CustomCourseworkModulesViewModalState.COURSE_ROSTER,
            }));
          }}
          title="Manage Roster"
          titleStyle={{ color: "#3E68FE" }}
        />
      </Menu>
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    maxWidth: 800,
    padding: 30,
    backgroundColor: "#fff",
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
});

export default CustomCourseworkModulesView;
